import { isPast } from 'date-fns';

import { IDatePickerData } from './types';

/** Re-filter today's slots against current time + order lead time. */
export const PICKUP_SLOT_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/** Re-render checkout pickup label and offer pickUpTime from the anchored fireOrderIn. */
export const CHECKOUT_PICKUP_TIME_DISPLAY_INTERVAL_MS = 60 * 1000;

const SLOT_MATCH_TOLERANCE_MS = 60_000;
const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** Round up to the next 5-minute boundary (e.g. 8:11:30 → 8:15:00). */
export function getDateRoundUpToNearestFiveMinutes(date: Date = new Date()): Date {
  return new Date(Math.ceil(date.getTime() / FIVE_MINUTES_MS) * FIVE_MINUTES_MS);
}

export type EarliestPickupTimeOptions = {
  /** Today's store open — prep time cannot end before open + lead time. */
  storeOpensAt?: Date;
  now?: Date;
};

/** Earliest pickup instant: max(now + lead time, store open + lead time), on a 5-minute boundary. */
export function getEarliestAvailablePickupTime(
  earliestPickupTimeInSeconds: number,
  { storeOpensAt, now = new Date() }: EarliestPickupTimeOptions = {}
): Date {
  const leadMs = earliestPickupTimeInSeconds * 1000;
  const afterNow = getDateRoundUpToNearestFiveMinutes(new Date(now.getTime() + leadMs));

  if (!storeOpensAt) {
    return afterNow;
  }

  const afterOpen = getDateRoundUpToNearestFiveMinutes(
    new Date(storeOpensAt.getTime() + leadMs)
  );

  return afterOpen.getTime() > afterNow.getTime() ? afterOpen : afterNow;
}

/**
 * Drop slots before now + earliestPickupTimeInSeconds. Future days are unchanged.
 */
export function filterPastPickupSlots(
  datePickerData: IDatePickerData[] | undefined,
  earliestPickupTimeInSeconds: number,
  earliestPickupOptions: EarliestPickupTimeOptions = {}
): IDatePickerData[] | undefined {
  if (!datePickerData) {
    return undefined;
  }

  const cutoff = getEarliestAvailablePickupTime(
    earliestPickupTimeInSeconds,
    earliestPickupOptions
  );

  return datePickerData.map(day => {
    const options = day.options.filter(option => option.time.getTime() >= cutoff.getTime());

    return {
      ...day,
      options,
      isUnavailable: day.isUnavailable || options.length === 0,
    };
  });
}

export function isPickupTimeStillValid(
  pickupTime: Date,
  earliestPickupTimeInSeconds: number,
  earliestPickupOptions: EarliestPickupTimeOptions = {}
): boolean {
  const cutoff = getEarliestAvailablePickupTime(
    earliestPickupTimeInSeconds,
    earliestPickupOptions
  );
  return pickupTime.getTime() >= cutoff.getTime() - SLOT_MATCH_TOLERANCE_MS;
}

/** True when the time still exists in the current picker options (after client filter). */
export function isPickupTimeInPickerSlots(
  datePickerData: IDatePickerData[] | undefined,
  pickupTime: Date
): boolean {
  if (!datePickerData?.length) {
    return false;
  }

  const targetMs = pickupTime.getTime();

  return datePickerData.some(day =>
    day.options.some(
      option => Math.abs(option.time.getTime() - targetMs) <= SLOT_MATCH_TOLERANCE_MS
    )
  );
}

/** Changes when the server schedule from pricing changes — not when the first slot advances. */
export function getOrderPickupScheduleKey(
  earliestPickupTimeInSeconds: number,
  availableTimeOptions: readonly string[] = [],
  rbiOrderId = ''
): string {
  const sortedOptions = [...availableTimeOptions].sort();
  return `${rbiOrderId}:${earliestPickupTimeInSeconds}:${sortedOptions.join('|')}`;
}

/** True when the confirmed time still exists in the filtered picker (single source of truth). */
export function isConfirmedPickupInPicker(
  datePickerData: IDatePickerData[] | undefined,
  pickupTime: Date
): boolean {
  return findPickerSelectionForTime(datePickerData, pickupTime) !== undefined;
}

/**
 * Pickup instant used for fireOrderIn — never a stale/past display time.
 * Falls back to defaultPickupTime when display is before the order minimum.
 */
export function resolveSchedulePickupTime({
  displayPickupTime,
  defaultPickupTime,
  datePickerData,
}: {
  displayPickupTime: Date | undefined;
  defaultPickupTime: Date | undefined;
  datePickerData?: IDatePickerData[];
}): Date | undefined {
  if (displayPickupTime && isConfirmedPickupInPicker(datePickerData, displayPickupTime)) {
    return displayPickupTime;
  }

  return defaultPickupTime;
}

/** Seconds until pickup for setFireOrderIn; 0 means fire immediately (ASAP / past). */
export function getFireOrderInSecondsForPickupTime(
  pickupTime: Date,
  now = new Date()
): number {
  const seconds = Math.round((pickupTime.getTime() - now.getTime()) / 1000);
  return Math.max(0, seconds);
}

export function getFirstAvailableSlotTime(
  options: IDatePickerData[] | undefined
): Date | undefined {
  const firstAvailableDay = options?.find(
    item => !item.isUnavailable && item.options.length > 0 && !isPast(item.options[0]?.time)
  );

  return firstAvailableDay?.options[0]?.time;
}

/**
 * Default pickup time for display and scheduling — driven by earliestPickupTimeInSeconds
 * and the first filtered slot in the picker, never by fireOrderIn.
 */
export function getDefaultPickupTime({
  earliestPickupTimeInSeconds,
  datePickerData,
  storeOpensAt,
}: {
  earliestPickupTimeInSeconds: number;
  datePickerData: IDatePickerData[] | undefined;
  storeOpensAt?: Date;
}): Date | undefined {
  const firstSlot = getFirstAvailableSlotTime(datePickerData);
  if (firstSlot) {
    return getDateRoundUpToNearestFiveMinutes(firstSlot);
  }

  if (earliestPickupTimeInSeconds > 0) {
    return getEarliestAvailablePickupTime(earliestPickupTimeInSeconds, { storeOpensAt });
  }

  return undefined;
}

/** Resolve day index and slot ISO for a pickup time already chosen or displayed. */
export function findPickerSelectionForTime(
  datePickerData: IDatePickerData[] | undefined,
  pickupTime: Date
): { dayIndex: number; slotIso: string } | undefined {
  if (!datePickerData?.length) {
    return undefined;
  }

  const targetMs = pickupTime.getTime();
  let bestDayIndex = 0;
  let bestSlot: Date | undefined;
  let bestDiff = Infinity;

  datePickerData.forEach((day, dayIndex) => {
    for (const { time } of day.options) {
      const diff = Math.abs(time.getTime() - targetMs);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestDayIndex = dayIndex;
        bestSlot = time;
      }
    }
  });

  if (!bestSlot || bestDiff > SLOT_MATCH_TOLERANCE_MS) {
    return undefined;
  }

  return { dayIndex: bestDayIndex, slotIso: bestSlot.toISOString() };
}
